import dayjs from "dayjs";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Badge } from "@/components/ui/badge";
import { QuestionInsights } from "./QuestionInsights";

function Breakdown({ title, rows, testId }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="admin-card p-6" data-testid={testId}>
      <p className="admin-kicker mb-4">{title}</p>
      {rows.length === 0 && <p className="text-sm text-white/50">No data yet.</p>}
      <ul className="space-y-3">
        {rows.map((row) => (
          <li key={row.key}>
            <div className="mb-1 flex items-center justify-between gap-3 text-sm">
              <span className="truncate">{row.label}</span>
              <span className="font-bold text-[#f2d28f]">{row.count}</span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-white/10">
              <div className="h-full rounded-full bg-gradient-to-r from-[#8f6a2b] to-[#f2d28f] transition-[width] duration-700" style={{ width: `${(row.count / max) * 100}%` }} />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SessionBreakdown({ rows }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="admin-card p-6" data-testid="breakdown-session">
      <p className="admin-kicker mb-4">By session</p>
      {rows.length === 0 && <p className="text-sm text-white/50">No data yet.</p>}
      <ul className="space-y-3">
        {rows.map((row) => (
          <li key={row.key}>
            <div className="mb-1 flex items-center justify-between gap-3 text-sm">
              <span className="truncate">{row.label}</span>
              <span className="font-bold text-[#f2d28f]">{row.count}</span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-white/10">
              <div className="h-full rounded-full bg-gradient-to-r from-[#8f6a2b] to-[#f2d28f] transition-[width] duration-700" style={{ width: `${(row.count / max) * 100}%` }} />
            </div>
            {(row.attended > 0 || row.no_show > 0) && (
              <div className="mt-1 text-[11px] text-white/45" data-testid={`session-attendance-${row.key}`}>
                <span className="text-emerald-300/80">{row.attended} attended</span> · <span className="text-amber-300/80">{row.no_show} no-show</span>
                {row.followed_up > 0 && <> · <span className="text-sky-300/80" data-testid={`session-followups-${row.key}`}>{row.followed_up} follow-up{row.followed_up === 1 ? "" : "s"} sent</span></>}
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function CampaignBreakdown({ rows }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="admin-card p-6" data-testid="breakdown-campaign">
      <p className="admin-kicker mb-1">By ad campaign</p>
      <p className="mb-4 text-xs text-white/45">Registrants who arrived via a UTM-tagged or Facebook ad link.</p>
      {rows.length === 0 && <p className="text-sm text-white/50" data-testid="breakdown-campaign-empty">No ad-tagged registrations yet. Add <code className="text-[#f2d28f]">?utm_source=facebook&amp;utm_campaign=…</code> to your ad URLs.</p>}
      <ul className="space-y-3">
        {rows.map((row) => (
          <li key={row.key} data-testid={`campaign-row-${row.key}`}>
            <div className="mb-1 flex items-center justify-between gap-3 text-sm">
              <span className="min-w-0">
                <span className="block truncate font-semibold">{row.campaign || "(no campaign name)"}</span>
                <span className="block truncate text-[11px] text-white/45">{row.source}{row.medium ? ` · ${row.medium}` : ""}{row.content.length ? ` · ${row.content.join(", ")}` : ""}</span>
              </span>
              <span className="font-bold text-[#f2d28f]">{row.count}</span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-white/10">
              <div className="h-full rounded-full bg-gradient-to-r from-[#7a3b8f] to-[#e39cf5] transition-[width] duration-700" style={{ width: `${(row.count / max) * 100}%` }} />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function Overview({ stats }) {
  if (!stats) return <p className="text-sm text-white/50">Loading overview…</p>;

  const byEvent = stats.by_event.map((e) => ({ key: e.iso, label: `${e.date} · ${e.language}`, count: e.count, attended: e.attended || 0, no_show: e.no_show || 0, followed_up: e.followed_up || 0 }));
  const byRef = stats.by_ref.map((r) => ({ key: r.code, label: r.code === "direct" ? "Direct / organic" : `${r.code} · ${r.referrer_name}`, count: r.count }));
  const byLanguage = stats.by_language.map((l) => ({ key: l.label, label: l.label, count: l.count }));
  const bySource = stats.by_source.map((s) => ({ key: s.label, label: s.label.replace(/_/g, " "), count: s.count }));
  const byTraffic = (stats.by_traffic || []).map((t) => ({ key: t.label, label: t.label, count: t.count }));
  const byCampaign = (stats.by_campaign || []).map((c, i) => ({ key: `${c.source}-${c.campaign || i}`.replace(/[^a-z0-9-]/gi, "_"), ...c }));
  const AGE_ORDER = ["Under 30", "30 – 39", "40 – 49", "50 – 59", "60 and above"];
  const byAge = [...(stats.by_age || [])]
    .sort((a, b) => (AGE_ORDER.indexOf(a.label) === -1 ? 99 : AGE_ORDER.indexOf(a.label)) - (AGE_ORDER.indexOf(b.label) === -1 ? 99 : AGE_ORDER.indexOf(b.label)))
    .map((a) => ({ key: a.label, label: a.label, count: a.count }));

  return (
    <div className="grid gap-5 lg:grid-cols-3">
      <div className="admin-card p-6 lg:col-span-2" data-testid="trend-chart">
        <div className="mb-5 flex items-center justify-between">
          <p className="admin-kicker">Registrations · last 14 days</p>
          <span className="text-xs text-white/50">Singapore time</span>
        </div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={stats.trend} margin={{ top: 4, right: 4, left: -22, bottom: 0 }}>
              <XAxis dataKey="label" tick={{ fill: "rgba(255,255,255,.5)", fontSize: 11 }} axisLine={false} tickLine={false} interval={1} />
              <YAxis allowDecimals={false} tick={{ fill: "rgba(255,255,255,.5)", fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip cursor={{ fill: "rgba(255,255,255,.05)" }} contentStyle={{ background: "#0b211a", border: "1px solid rgba(255,255,255,.12)", borderRadius: 12, fontSize: 12 }} labelStyle={{ color: "#f2d28f" }} itemStyle={{ color: "#fff" }} />
              <Bar dataKey="count" name="Registrations" fill="#d9ad57" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="admin-card p-6" data-testid="recent-registrations">
        <p className="admin-kicker mb-4">Most recent</p>
        {stats.recent.length === 0 && <p className="text-sm text-white/50">No registrations yet.</p>}
        <ul className="space-y-3">
          {stats.recent.map((reg) => (
            <li key={reg.id} className="flex items-start justify-between gap-3 border-b border-white/8 pb-3 last:border-0 last:pb-0">
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold">{reg.full_name}</div>
                <div className="truncate text-xs text-white/50">{reg.event_date}</div>
              </div>
              <div className="text-right">
                <Badge variant="outline" className="border-white/15 text-[10px] uppercase tracking-wide text-[#f2d28f]">{reg.event_language}</Badge>
                <div className="mt-1 text-[11px] text-white/40">{dayjs(reg.created_at).format("DD MMM, HH:mm")}</div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <SessionBreakdown rows={byEvent} />
      <QuestionInsights data={stats.by_question} />
      <Breakdown title="By referral link" rows={byRef} testId="breakdown-ref" />
      <CampaignBreakdown rows={byCampaign} />
      <Breakdown title="By traffic source" rows={byTraffic} testId="breakdown-traffic" />
      <div className="grid gap-5">
        <Breakdown title="By age range" rows={byAge} testId="breakdown-age" />
        <Breakdown title="By language" rows={byLanguage} testId="breakdown-language" />
        <Breakdown title="By form source" rows={bySource} testId="breakdown-source" />
      </div>
    </div>
  );
}
